import { FC, useCallback, useRef } from 'react';
import { useSubscriptionCancel } from '../hooks/SubscriptionCancel/useSubscriptionCancel';
import { useCancelSurvey } from '../hooks/SubscriptionCancel/useCancelSurvey';
import { useSubscriptionCancelForm } from '../hooks/SubscriptionCancel/useSubscriptionCancelForm';
import { useLoadingAny } from '../hooks/common/useLoadingAny';
import { useLogging } from '../hooks/common/useLogging';
import { useEmitResetWindow } from '../hooks/common/useEmitResetWindow';
import { Setting } from '../App';

import { SUBSCRIPTION_NAME } from '../constants';
import { SectionTitle } from '../components/common/SectionTitle';
import { Description } from '../components/common/Description';
import { HorizontalRule } from '../components/common/HorizontalRule';
import { NavigationWrapper } from '../components/common/NavigationWrapper';
import { Button } from '../components/common/Button';
import { CancelSurvey } from '../components/SubscriptionCancel/CancelSurvey';
import { ExtraField } from '../components/SubscriptionCancel/ExtraField';
import { useDeliveryDate } from '../hooks/SubscriptionDetail/useDeliveryDate';

export const SubscriptionCancel: FC<{ setting: Setting }> = ({ setting }) => {

  const { subscriptionRef, token } = setting;
  const [{ subscription, loadingSubscriptions }, { getSubscription }] = subscriptionRef;

  const { cancelSurvey, loadingCancelSurvey } = useCancelSurvey(token);
  const [{state, isAllValid}, {handleChange, handleBlur}] = useSubscriptionCancelForm(cancelSurvey);
  const [loadingSubscriptionCancel, cancelSubscription] = 
    useSubscriptionCancel(subscription?.subscriptionContractId, token);

  const [ { dates } ] = useDeliveryDate(subscription, token, getSubscription);
  const { isBanningModify } = dates;

  const loadingAny = useLoadingAny([
    loadingSubscriptions,
    loadingSubscriptionCancel,
    loadingCancelSurvey
  ]);

  const handleSubmit = useCallback(() => {

    if (cancelSurvey?.cancelSurveyEnable && state) {
      const selectedState = state[0].value as string[];
      const selected = selectedState.join(',');
      const extraField = state[1].value as string;
      cancelSubscription(selected, extraField);
    } else {
      cancelSubscription();
    }
  }, [state, cancelSurvey]);

  const rootElementReference = useRef<HTMLDivElement | null>(null);
  useEmitResetWindow(rootElementReference, [cancelSurvey, dates]);

  useLogging('SubscriptionCancel', [cancelSurvey, state, isAllValid]);

  return (
    <div 
      className={`appContainer subscriptionCancel`}
      ref={rootElementReference}
    >
      <SectionTitle>{SUBSCRIPTION_NAME}を解約する</SectionTitle>
      { cancelSurvey?.cancelSurveyEnable && (
        <>
          { cancelSurvey?.cancelSurveyLeadText && (
            <Description>{cancelSurvey.cancelSurveyLeadText}</Description>
          )}
          { state[0] && (
            <CancelSurvey state={state[0]} onChange={handleChange} onBlur={handleBlur}/>
          )}
          <HorizontalRule size="small"/>
        </>
      )}
      { cancelSurvey?.cancelSurveyExtraField && (
        <>
          <Description>その他にお気づきになった点があれば、こちらにご記入ください。</Description>
          <ExtraField state={state[1]} onChange={handleChange} onBlur={handleBlur}/>
          <HorizontalRule size="small"/>
        </>
      )}
      { isBanningModify && (
        <div style={{ textAlign: 'center' }}>
          解約は次回決済日の{subscription?.shop.allowModifyDays || 'x'}日前まで可能です。<br/>
          お手数ですが、次回決済完了後に改めてこちらのページで処理を行ってください。
        </div>
      )}
      <NavigationWrapper>
        <Button onClick={handleSubmit} disabled={
          isBanningModify || (
            cancelSurvey?.cancelSurveyEnable &&
            cancelSurvey?.cancelSurveyRequire &&
            !isAllValid
          ) || loadingAny
        } type="primary">解約する</Button>
        <Button internalLink="/" type="secondary">もどる</Button>
      </NavigationWrapper>
    </div>
  );
};